import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { Loader2, Star, Phone, MapPin, Check, ArrowRight, X, Wrench, Sparkles, ShieldCheck, Clock, Award, ThumbsUp, MoveHorizontal, Rocket, Gauge, AlertTriangle, CreditCard, Zap, Infinity as InfinityIcon } from "lucide-react";

function BeforeAfterSlider({ beforeUrl, afterUrl, accent }) {
  const ref = useRef(null);
  const [pos, setPos] = useState(52);
  const [w, setW] = useState(0);
  const [drag, setDrag] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setW(el.clientWidth));
    ro.observe(el);
    setW(el.clientWidth);
    return () => ro.disconnect();
  }, []);

  const move = (clientX) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setPos(Math.min(98, Math.max(2, ((clientX - r.left) / r.width) * 100)));
  };

  return (
    <div className="max-w-5xl mx-auto px-6 pt-6" data-testid="before-after">
      <div className="flex items-center justify-center gap-2 mb-3 text-sm font-semibold text-slate-500">
        <MoveHorizontal className="w-4 h-4" style={{ color: accent }} /> Drag to compare — your site today vs. your new site
      </div>
      <div ref={ref} className="relative w-full aspect-[16/10] rounded-2xl overflow-hidden border border-slate-200 shadow-2xl select-none bg-slate-100"
        onPointerMove={(e) => drag && move(e.clientX)}
        onPointerUp={() => setDrag(false)} onPointerLeave={() => setDrag(false)}>
        <iframe title="after" src={afterUrl} className="absolute inset-0 w-full h-full pointer-events-none" style={{ border: 0 }} scrolling="no" />
        <span className="absolute top-3 right-3 z-10 px-2.5 py-1 rounded-full text-[11px] font-bold text-white" style={{ background: accent }}>AFTER · your new site</span>
        <div className="absolute inset-0 overflow-hidden" style={{ width: `${pos}%` }}>
          <div className="absolute inset-0" style={{ width: w ? `${w}px` : "100%" }}>
            {beforeUrl
              ? <iframe title="before" src={beforeUrl} className="absolute inset-0 w-full h-full pointer-events-none bg-white" style={{ border: 0 }} scrolling="no" />
              : <div className="absolute inset-0 flex items-center justify-center bg-slate-200 text-slate-500 text-sm font-medium">No website today</div>}
            <div className="absolute inset-0 bg-slate-900/5" />
          </div>
          <span className="absolute top-3 left-3 z-10 px-2.5 py-1 rounded-full text-[11px] font-bold bg-slate-800 text-white">BEFORE · today</span>
        </div>
        <div className="absolute top-0 bottom-0 z-20 -ml-3 w-6 cursor-ew-resize flex items-center justify-center" style={{ left: `${pos}%` }}
          onPointerDown={(e) => { e.currentTarget.setPointerCapture?.(e.pointerId); setDrag(true); }}>
          <div className="w-0.5 h-full bg-white/90 shadow" />
          <div className="absolute w-9 h-9 rounded-full bg-white shadow-lg flex items-center justify-center" style={{ color: accent }}><MoveHorizontal className="w-4 h-4" /></div>
        </div>
      </div>
      <p className="text-center text-[11px] text-slate-400 mt-2">If your current site can't be shown here, your browser is blocking the embed — the new site is fully live-ready either way.</p>
    </div>
  );
}

function SignalProof({ signals, accent }) {
  if (!signals) return null;
  const ps = signals.pagespeed || {};
  const tech = signals.tech || {};
  const rev = signals.reviews || {};
  const chips = [];
  if (ps.score != null) chips.push({ icon: Gauge, label: `Mobile speed ${ps.score}/100`, bad: ps.score < 90 });
  if (tech.platform) chips.push({ icon: Wrench, label: tech.platform, bad: !!tech.dated_builder });
  if (tech.mobile_ready === false) chips.push({ icon: AlertTriangle, label: "Not mobile-ready", bad: true });
  if (tech.https === false) chips.push({ icon: ShieldCheck, label: "No HTTPS", bad: true });
  if (rev.review_count != null) chips.push({ icon: Star, label: `${rev.rating ? rev.rating + "★ · " : ""}${rev.review_count} reviews`, bad: rev.weak });
  if (!chips.length && !signals.proof_line) return null;
  return (
    <div className="max-w-5xl mx-auto px-6 pt-6" data-testid="signal-proof">
      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 shadow-sm">
        <div className="flex items-center gap-2 mb-3">
          <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide px-2.5 py-1 rounded-full text-white" style={{ background: accent }}>
            <Zap className="w-3.5 h-3.5" /> Free audit
          </span>
          {signals.opportunity != null && (
            <span className="text-xs font-semibold text-slate-500">Opportunity score {signals.opportunity}/100</span>
          )}
        </div>
        {signals.proof_line && <p className="text-sm text-slate-700 font-medium mb-3">{signals.proof_line}</p>}
        <div className="flex flex-wrap gap-2">
          {chips.map((c, i) => {
            const Icon = c.icon;
            return (
              <span key={i} data-testid={`signal-chip-${i}`}
                className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border ${c.bad ? "bg-rose-50 text-rose-700 border-rose-200" : "bg-white text-slate-600 border-slate-200"}`}>
                <Icon className="w-3.5 h-3.5" /> {c.label}
              </span>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function PricingTiers({ tiers, accent, discountPct }) {
  if (!tiers || !tiers.length) return null;
  const backend = process.env.REACT_APP_BACKEND_URL || "";
  const iconFor = { deposit: CreditCard, lifetime: InfinityIcon, monthly: Clock };
  return (
    <section id="pricing" className="max-w-5xl mx-auto px-6 py-14" data-testid="pricing-tiers">
      <h2 className="text-2xl font-extrabold text-center">Ready to make it yours?</h2>
      <p className="text-center text-sm text-slate-500 mt-1">Pick a plan and you're live in a day. The deposit is 100% refundable.</p>
      <div className="mt-8 grid sm:grid-cols-3 gap-5">
        {tiers.map((t) => {
          const Icon = iconFor[t.tier] || CreditCard;
          const featured = t.tier === "lifetime";
          return (
            <div key={t.tier} data-testid={`tier-${t.tier}`}
              className={`rounded-2xl p-6 border shadow-sm flex flex-col ${featured ? "border-transparent shadow-xl scale-[1.03]" : "border-slate-200 bg-white"}`}
              style={featured ? { background: `linear-gradient(160deg, ${accent}, ${accent}cc)`, color: "#fff" } : {}}>
              <div className="flex items-center gap-2">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${featured ? "bg-white/20" : "text-white"}`} style={featured ? {} : { background: accent }}>
                  <Icon className="w-5 h-5" style={featured ? { color: "#fff" } : {}} />
                </div>
                <div className="font-bold">{t.name}</div>
              </div>
              <div className="mt-4 flex items-baseline gap-1">
                <span className="text-3xl font-extrabold">${Math.round(t.amount)}</span>
                {t.recurring && <span className={`text-sm ${featured ? "text-white/80" : "text-slate-400"}`}>/mo</span>}
                {t.discounted && <span className={`ml-1 text-sm line-through ${featured ? "text-white/60" : "text-slate-400"}`}>${Math.round(t.list_amount)}</span>}
              </div>
              {t.discounted && <div className="mt-1 text-xs font-bold" style={featured ? { color: "#fff" } : { color: accent }}>{discountPct}% off — today only</div>}
              <p className={`mt-2 text-sm ${featured ? "text-white/85" : "text-slate-500"}`}>{t.blurb}</p>
              <a data-testid={`tier-pay-${t.tier}`} href={`${backend}${t.pay_url}`} target="_blank" rel="noreferrer"
                className={`mt-5 inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold ${featured ? "bg-white" : "text-white"}`}
                style={featured ? { color: accent } : { background: accent }}>
                Choose {t.name} <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          );
        })}
      </div>
    </section>
  );
}

export default function DemoPreview() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const embed = params.get("embed") === "1";
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [claimOpen, setClaimOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/outreach/preview/${id}`).then((r) => setData(r.data)).catch(() => setErr(true));
  }, [id]);

  const claim = async () => {
    setBusy(true);
    try { await api.post(`/outreach/claim/${id}`, form); setSent(true); }
    catch { setBusy(false); }
  };

  useSeo({ title: data ? `${data.business_name} — website preview` : "Website preview", noindex: true });

  if (err) return <div className="min-h-screen flex items-center justify-center bg-slate-950 text-slate-400">Preview not found.</div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center bg-slate-950"><Loader2 className="w-6 h-6 animate-spin text-slate-500" /></div>;

  const accent = data.accent || "#0EA5E9";
  const demo = data.demo || {};
  const services = demo.services || [];
  const cta = demo.cta || "Get a Quote";

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="demo-preview" style={{ "--accent": accent }}>
      {/* Sticky claim-to-launch banner */}
      {!embed && (
        <div className="sticky top-0 z-40 w-full text-white shadow-lg" style={{ background: accent }} data-testid="claim-banner">
          <div className="max-w-5xl mx-auto px-4 py-2.5 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-sm font-semibold min-w-0">
              <Sparkles className="w-4 h-4 shrink-0" />
              <span className="truncate">Like this site? Claim ownership & launch it live with SSL.</span>
            </div>
            <button data-testid="claim-btn-banner" onClick={() => setClaimOpen(true)}
              className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-white px-4 py-1.5 text-sm font-bold" style={{ color: accent }}>
              <Rocket className="w-4 h-4" /> Claim & Launch
            </button>
          </div>
        </div>
      )}

      {/* Before/After comparison slider */}
      {!embed && (
        <BeforeAfterSlider beforeUrl={data.current_site} afterUrl={`/preview/${id}?embed=1`} accent={accent} />
      )}

      {/* Buying-signal proof (free audit) */}
      {!embed && <SignalProof signals={data.signals} accent={accent} />}

      {/* Nav */}
      <header className="max-w-5xl mx-auto flex items-center justify-between px-6 py-5">
        <div className="font-extrabold text-lg tracking-tight" style={{ color: accent }}>{data.business_name}</div>
        <nav className="hidden sm:flex items-center gap-6 text-sm text-slate-600">
          <span>Services</span><span>About</span><span>Contact</span>
          <span className="px-4 py-2 rounded-full text-white text-sm font-semibold" style={{ background: accent }}>{demo.cta || "Get a Quote"}</span>
        </nav>
      </header>

      {/* Hero */}
      <section className="max-w-5xl mx-auto px-6 pt-10 pb-16 grid md:grid-cols-2 gap-10 items-center">
        <div>
          <h1 className="text-4xl sm:text-5xl font-extrabold leading-tight tracking-tight">{demo.headline || data.business_name}</h1>
          <p className="mt-4 text-lg text-slate-600">{demo.subheadline}</p>
          <div className="mt-7 flex items-center gap-3">
            <button data-testid="claim-btn-hero" onClick={() => setClaimOpen(true)} className="px-6 py-3 rounded-full text-white font-semibold inline-flex items-center gap-2" style={{ background: accent }}>{cta} <ArrowRight className="w-4 h-4" /></button>
            {data.phone && <a className="px-5 py-3 rounded-full border border-slate-300 font-semibold inline-flex items-center gap-2"><Phone className="w-4 h-4" /> {data.phone}</a>}
          </div>
          {data.rating && (
            <div className="mt-6 flex items-center gap-2 text-sm text-slate-500">
              <span className="flex items-center gap-0.5 text-amber-500">{[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4" fill={i < Math.round(data.rating) ? "currentColor" : "none"} />)}</span>
              {data.rating} {data.review_count ? `(${data.review_count} reviews)` : ""}
            </div>
          )}
        </div>
        <div className="rounded-2xl h-72 md:h-80 shadow-2xl overflow-hidden flex items-center justify-center text-white text-6xl font-black"
             style={{ background: `linear-gradient(135deg, ${accent}, ${accent}22)` }}>
          {data.hero_image
            ? <img src={data.hero_image} alt={data.business_name} className="w-full h-full object-cover" />
            : (data.business_name || "?").slice(0, 1)}
        </div>
      </section>

      {/* Services */}
      {services.length > 0 && (
        <section className="bg-slate-50 py-16">
          <div className="max-w-5xl mx-auto px-6">
            <h2 className="text-2xl font-extrabold text-center">What we do</h2>
            <div className="mt-8 grid sm:grid-cols-3 gap-5">
              {services.map((s, i) => {
                const Icon = [Wrench, Sparkles, ShieldCheck][i % 3];
                return (
                  <div key={i} className="bg-white rounded-xl p-6 shadow-sm border border-slate-100">
                    <div className="w-11 h-11 rounded-lg flex items-center justify-center text-white mb-3" style={{ background: accent }}><Icon className="w-5 h-5" /></div>
                    <div className="font-bold">{s}</div>
                    <p className="text-sm text-slate-500 mt-1">Reliable, professional {String(s).toLowerCase()} done right the first time.</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Why choose us */}
      <section className="max-w-5xl mx-auto px-6 py-14">
        <div className="grid sm:grid-cols-3 gap-5 text-center">
          {[[Clock, "Fast response"], [Award, "Top-rated locally"], [ThumbsUp, "Satisfaction guaranteed"]].map(([Icon, label], i) => (
            <div key={i} className="flex flex-col items-center">
              <div className="w-12 h-12 rounded-full flex items-center justify-center mb-2" style={{ background: `${accent}1a`, color: accent }}><Icon className="w-6 h-6" /></div>
              <div className="font-semibold text-slate-800">{label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Testimonials (sample) */}
      <section className="bg-slate-50 py-16">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-2xl font-extrabold text-center">What customers say</h2>
          <p className="text-center text-xs text-slate-400 mt-1">Sample reviews — shown for preview</p>
          <div className="mt-8 grid sm:grid-cols-2 gap-5">
            {[
              { q: `${data.business_name} was fantastic — professional, on time, and the results exceeded expectations.`, n: "Jamie R." },
              { q: `Best experience I've had in ${data.city || "town"}. Friendly team and top-quality work. Highly recommend!`, n: "Alex M." },
            ].map((t, i) => (
              <div key={i} className="bg-white rounded-xl p-6 shadow-sm border border-slate-100">
                <div className="flex text-amber-500 mb-2">{[...Array(5)].map((_, j) => <Star key={j} className="w-4 h-4" fill="currentColor" />)}</div>
                <p className="text-slate-700">"{t.q}"</p>
                <div className="mt-3 text-sm font-semibold text-slate-500">— {t.n}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About */}
      <section className="max-w-3xl mx-auto px-6 py-16 text-center">
        <h2 className="text-2xl font-extrabold">About {data.business_name}</h2>
        <p className="mt-4 text-slate-600 text-lg">{demo.about}</p>
        {data.address && <p className="mt-4 text-sm text-slate-500 inline-flex items-center gap-1"><MapPin className="w-4 h-4" /> {data.address}</p>}
      </section>

      {/* One-tap checkout tiers */}
      {!embed && <PricingTiers tiers={data.tiers} accent={accent} discountPct={data.discount_pct} />}

      {/* CTA */}
      <section className="py-16 text-center text-white" style={{ background: accent }}>
        <h2 className="text-3xl font-extrabold">Ready to get started?</h2>
        <p className="mt-2 opacity-90">Book {data.business_name} today.</p>
        <button data-testid="claim-btn-footer" onClick={() => setClaimOpen(true)} className="mt-6 px-8 py-3 rounded-full bg-white font-bold" style={{ color: accent }}>{cta}</button>
      </section>

      <footer className="py-8 text-center text-xs text-slate-400">
        Demo preview created by Kleen-G · <button onClick={() => setClaimOpen(true)} className="font-medium" style={{ color: accent }}>Want this live for {data.business_name}? Claim it →</button>
      </footer>

      {claimOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setClaimOpen(false)} data-testid="claim-modal">
          <div className="w-full max-w-md bg-white rounded-2xl p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-1">
              <h3 className="text-xl font-extrabold" style={{ color: accent }}>Claim your site</h3>
              <button onClick={() => setClaimOpen(false)} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
            </div>
            {sent ? (
              <div className="py-8 text-center" data-testid="claim-success">
                <div className="w-12 h-12 rounded-full mx-auto flex items-center justify-center text-white mb-3" style={{ background: accent }}><Check className="w-6 h-6" /></div>
                <p className="font-bold text-slate-800">Thank you!</p>
                <p className="text-slate-500 text-sm mt-1">We'll be in touch shortly to get {data.business_name} live.</p>
              </div>
            ) : (
              <>
                <p className="text-sm text-slate-500 mb-4">Love the preview? Tell us where to reach you and we'll make it live.</p>
                <div className="space-y-3">
                  <input data-testid="claim-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-slate-900 focus:outline-none focus:ring-2" style={{ "--tw-ring-color": accent }} />
                  <input data-testid="claim-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email or phone" className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-slate-900 focus:outline-none focus:ring-2" />
                  <textarea data-testid="claim-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Anything you'd like changed? (optional)" rows={3} className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-slate-900 focus:outline-none focus:ring-2" />
                </div>
                <button data-testid="claim-submit" onClick={claim} disabled={busy || (!form.name && !form.email)} className="mt-4 w-full py-3 rounded-full text-white font-bold inline-flex items-center justify-center gap-2 disabled:opacity-50" style={{ background: accent }}>
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} {cta}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
